import { nextTick } from "vue";

const logFocusNavError = (context: string, error: unknown) =>
  console.error(`[focus-nav] ${context}`, error);

const hasVisibleClientRect = (el?: HTMLElement | null): el is HTMLElement => {
  if (!el) {
    return false;
  }
  try {
    const rect = el.getBoundingClientRect?.();
    if (rect && (rect.width > 0 || rect.height > 0)) {
      return true;
    }
    return el.getClientRects?.().length > 0;
  } catch (error) {
    logFocusNavError("Failed to compute element client rect", error);
    return false;
  }
};

export const isElementVisible = (el?: HTMLElement | null): el is HTMLElement =>
  !!el && (el.offsetParent !== null || hasVisibleClientRect(el));

export const selectAllText = (el: HTMLElement) => {
  try {
    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  } catch (error) {
    logFocusNavError("Failed to select all text", error);
  }
};

export const focusAndSelectElement = (el: HTMLElement) => {
  nextTick(() => {
    try {
      if (el.isContentEditable) {
        const isLabelOrShortLabel =
          el.classList?.contains("label-input") || el.classList?.contains("short-label");
        const initialSanitized = (el.textContent || "").replace(/\u200B/g, "");

        // Keep placeholder visible on empty label/short-label; place caret without inserting text
        if (isLabelOrShortLabel && initialSanitized.length === 0) {
          el.focus();
          const range = document.createRange();
          range.selectNodeContents(el);
          range.collapse(true);
          const sel = window.getSelection();
          sel?.removeAllRanges();
          sel?.addRange(range);
          return;
        }

        if (!el.firstChild || el.firstChild.nodeType !== Node.TEXT_NODE) {
          const node = document.createTextNode(el.textContent || "");
          el.textContent = "";
          el.appendChild(node);
        }
        const textNode = el.firstChild as Text;
        el.focus();
        const sanitized = (textNode.textContent || "").replace(/\u200B/g, "");
        if (sanitized.length === 0) {
          // Ensure caret is visible even when content is empty
          if (!textNode.textContent) {
            textNode.textContent = "\u200B";
          }
          const range = document.createRange();
          range.setStart(textNode, 0);
          range.collapse(true);
          const sel = window.getSelection();
          sel?.removeAllRanges();
          sel?.addRange(range);
        } else {
          selectAllText(el);
        }
        return;
      }
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
        el.focus({ preventScroll: true });
        el.select?.();
        return;
      }
      el.focus?.({ preventScroll: true } as FocusOptions);
    } catch (error) {
      logFocusNavError("Failed to focus element during tab loop", error);
    }
  });
};

export const handleActiveElementTabLoop = (event: KeyboardEvent) => {
  if (event.key !== "Tab" || event.defaultPrevented) {
    return;
  }

  const target = (event.target as HTMLElement) || null;
  if (!target) {
    return;
  }

  const activeItem = target.closest(
    ".form-builder--item[data-active='true']"
  ) as HTMLElement | null;
  if (!activeItem) {
    return;
  }

  const selectors = [
    ".label-input[contenteditable='true']",
    ".form-placeholder",
    "input:not([type='hidden']):not([type='checkbox']):not([type='radio']):not([disabled])",
    "textarea:not([disabled])",
    ".short-label[contenteditable='true']",
    "label[contenteditable='true']",
    ".ProseMirror[contenteditable='true']",
  ];

  const rows = Array.from(activeItem.querySelectorAll("tr")) as HTMLElement[];
  const rowWise: HTMLElement[] = [];
  rows.forEach((row) => {
    const rowLabel = row.querySelector("label[contenteditable='true']") as HTMLElement | null;
    const rowInput = row.querySelector(
      "input:not([type='hidden']):not([type='checkbox']):not([type='radio']):not([disabled])"
    ) as HTMLElement | null;
    if (rowLabel && isElementVisible(rowLabel)) {
      rowWise.push(rowLabel);
    }
    if (rowInput && isElementVisible(rowInput)) {
      rowWise.push(rowInput);
    }
  });

  const selectorString = selectors.join(",");
  const seen = new Set<HTMLElement>();
  const ordered: HTMLElement[] = [];

  const appendMissingOptionLabels = () => {
    const optionLabels = Array.from(
      activeItem.querySelectorAll("label[contenteditable='true']")
    ) as HTMLElement[];
    if (!optionLabels.length) {
      return;
    }
    const shortLabelIndex = ordered.findIndex((el) => el.classList.contains("short-label"));
    let insertAt = shortLabelIndex === -1 ? ordered.length : shortLabelIndex;
    optionLabels.forEach((el) => {
      if (seen.has(el)) {
        return;
      }
      if (!isElementVisible(el) && !hasVisibleClientRect(el)) {
        return;
      }
      ordered.splice(insertAt, 0, el);
      seen.add(el);
      insertAt += 1;
    });
  };

  if (rowWise.length) {
    const mainLabel =
      (activeItem.querySelector(".label-input[contenteditable='true']") as HTMLElement | null) ||
      (activeItem.querySelector("label[contenteditable='true']") as HTMLElement | null);
    const shortLabel = activeItem.querySelector(
      ".short-label[contenteditable='true']"
    ) as HTMLElement | null;

    if (mainLabel && isElementVisible(mainLabel)) {
      ordered.push(mainLabel);
      seen.add(mainLabel);
    }

    rowWise.forEach((el) => {
      if (!seen.has(el)) {
        ordered.push(el);
        seen.add(el);
      }
    });

    if (shortLabel && isElementVisible(shortLabel)) {
      ordered.push(shortLabel);
      seen.add(shortLabel);
    }
  } else {
    const walker = document.createTreeWalker(activeItem, NodeFilter.SHOW_ELEMENT, {
      acceptNode: (node) => {
        const el = node as HTMLElement;
        if (!el.matches(selectorString)) {
          return NodeFilter.FILTER_SKIP;
        }
        if (!isElementVisible(el) || el.closest("[data-ignore-tab-loop]")) {
          return NodeFilter.FILTER_SKIP;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    while (walker.nextNode()) {
      const el = walker.currentNode as HTMLElement;
      if (!seen.has(el)) {
        seen.add(el);
        ordered.push(el);
      }
    }
  }

  appendMissingOptionLabels();
  if (!ordered.length) {
    return;
  }

  const targetEditable = target.closest("[contenteditable='true']");
  const currentIndex = ordered.findIndex(
    (el) => el === target || el.contains(target) || (targetEditable && el === targetEditable)
  );
  if (currentIndex === -1) {
    return;
  }

  const direction = event.shiftKey ? -1 : 1;
  const nextIndex = (currentIndex + direction + ordered.length) % ordered.length;

  event.preventDefault();
  event.stopPropagation();
  (event as any)?.stopImmediatePropagation?.();

  focusAndSelectElement(ordered[nextIndex]);
};

export const addActiveTabLoopListener = () => {
  try {
    window?.addEventListener("keydown", handleActiveElementTabLoop);
  } catch (error) {
    logFocusNavError("Failed to add active tab loop listener", error);
  }
};

export const removeActiveTabLoopListener = () => {
  try {
    window?.removeEventListener("keydown", handleActiveElementTabLoop);
  } catch (error) {
    logFocusNavError("Failed to remove active tab loop listener", error);
  }
};

export const stopTabEvent = (event: KeyboardEvent) => {
  event.preventDefault();
  event.stopPropagation();
  (event as any)?.stopImmediatePropagation?.();
};

export const focusLabelElement = (el?: HTMLElement | null): boolean => {
  if (!el) {
    return false;
  }
  try {
    el.focus?.();
    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    return true;
  } catch (error) {
    logFocusNavError("Failed to focus label", error);
  }
  return false;
};

export const focusInputElement = (el?: HTMLInputElement | null): boolean => {
  if (!el) {
    return false;
  }
  el.focus?.({ preventScroll: true });
  try {
    el.select?.();
    return true;
  } catch (error) {
    logFocusNavError("Failed to focus input", error);
  }
  return false;
};

export const focusFirstLabelElement = (labels: Array<HTMLElement | null | undefined>) =>
  focusLabelElement(labels?.[0]);

export const focusLastInputOrLabel = (
  inputs: Array<HTMLInputElement | null | undefined>,
  labels: Array<HTMLElement | null | undefined>,
  count: number
) => {
  const lastInput = inputs?.filter(Boolean).slice(-1)[0] as HTMLInputElement | undefined;
  if (focusInputElement(lastInput)) {
    return true;
  }
  return focusLabelElement(labels?.[Math.max(0, count - 1)]);
};

export const focusMainLabelFromShort = (shortLabelEl?: HTMLElement | null) => {
  try {
    const container = shortLabelEl?.closest(".form-builder--item") as HTMLElement | null;
    const mainLabel = container?.querySelector(
      ".label-input[contenteditable='true']"
    ) as HTMLElement | null;
    return focusLabelElement(mainLabel);
  } catch (error) {
    logFocusNavError("Failed to focus main label from short label", error);
  }
  return false;
};

export const handleTextboxTabFromLabel = (
  event: KeyboardEvent,
  index: number,
  getInputRef: (i: number) => HTMLInputElement | null,
  getLabelRef: (i: number) => HTMLElement | null
) => {
  const currentInput = getInputRef(index);
  const prevInput = getInputRef(index - 1);
  const nextLabel = getLabelRef(index + 1);

  if (event.shiftKey) {
    if (prevInput && focusInputElement(prevInput)) {
      stopTabEvent(event);
      return;
    }
    return;
  }

  if (currentInput && focusInputElement(currentInput)) {
    stopTabEvent(event);
    return;
  }

  if (nextLabel && focusLabelElement(nextLabel)) {
    stopTabEvent(event);
  }
};

export const handleTextboxTabFromInput = (
  event: KeyboardEvent,
  index: number,
  getInputRef: (i: number) => HTMLInputElement | null,
  getLabelRef: (i: number) => HTMLElement | null,
  shortLabelEl?: HTMLElement | null
) => {
  const currentLabel = getLabelRef(index);
  const nextLabel = getLabelRef(index + 1);
  const prevLabel = getLabelRef(index - 1);

  if (event.shiftKey) {
    if (currentLabel && focusLabelElement(currentLabel)) {
      stopTabEvent(event);
      return;
    }
    if (prevLabel && focusLabelElement(prevLabel)) {
      stopTabEvent(event);
    }
    return;
  }

  if (nextLabel && focusLabelElement(nextLabel)) {
    stopTabEvent(event);
    return;
  }

  if (shortLabelEl && focusLabelElement(shortLabelEl)) {
    stopTabEvent(event);
  }
};

export const handleShortLabelTabNav = (
  event: KeyboardEvent,
  shortLabelEl: HTMLElement | null,
  inputs: Array<HTMLInputElement | null | undefined>,
  labels: Array<HTMLElement | null | undefined>,
  count: number,
  handleSafariCursor?: (e: Event) => void
) => {
  handleSafariCursor?.(event);
  if (event.key !== "Tab") {
    return;
  }
  stopTabEvent(event);

  if (event.shiftKey) {
    focusLastInputOrLabel(inputs, labels, count);
  } else if (!focusMainLabelFromShort(shortLabelEl)) {
    focusFirstLabelElement(labels as HTMLElement[]);
  }
};
